// ─────────────────────────────────────────────
// KLOTHIQ — Loading & Skeleton Components (Dark)
// ─────────────────────────────────────────────
// FEAT-LOADER-001: internals now delegate to BrandLoader (the shared
// "Teal Pulse Reveal" ring + glow, no logo at this size — spec FR-006).
// Same exported name and `message`/`className` prop contract as before,
// so none of this component's 20 call sites across the app need to change.

import { cn } from '@/lib/utils';
import BrandLoader from '@/components/common/BrandLoader';

interface LoadingStateProps {
  message?: string;
  className?: string;
}

export default function LoadingState({ message = 'Loading...', className }: LoadingStateProps) {
  return <BrandLoader variant="inline" message={message} className={className} />;
}

/**
 * Dark-optimized skeleton card placeholder
 */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn('animate-pulse rounded-xl border border-white/5 bg-card p-5', className)}>
      <div className="mb-3 h-2.5 w-20 rounded-full bg-white/8" />
      <div className="h-7 w-28 rounded-lg bg-white/10" />
      <div className="mt-3 h-2 w-16 rounded-full bg-white/5" />
    </div>
  );
}

/**
 * Dark-optimized skeleton table rows
 */
export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="animate-pulse space-y-2">
      {/* Header row */}
      <div className="h-10 rounded-xl bg-white/5" />
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-xl bg-white/[0.03]"
          style={{ opacity: 1 - i * 0.1 }}
        />
      ))}
    </div>
  );
}

/**
 * 4-column metric card skeleton (Dashboard KPI rows)
 */
export function SkeletonMetricGrid({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}
